import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SOURCE_ID = 'refresh-labs-gwp';
const DRUM_SOURCE_ID = 'refresh-labs-drum-pro';
const CART_SETTLE_DELAYS = Object.freeze([120, 900]);

// Both Refresh Labs gift programs share one cart-mutation queue. This prevents
// the consumer threshold gift and the commercial drum bonus from racing each
// other when Horizon dispatches several cart events in quick succession.
const cartCoordinator = window.__refreshLabsCartCoordinator || {
  tail: Promise.resolve(),
  run(task) {
    const result = this.tail.then(task, task);
    this.tail = result.catch(() => undefined);
    return result;
  },
};
window.__refreshLabsCartCoordinator = cartCoordinator;
const DEFAULT_CONFIG = Object.freeze({
  enabled: false,
  giftVariantId: 47253628027072,
  qualifyingProductIds: [8336931324096, 8561578705088],
  collectionOffers: [],
  currentProductId: 0,
  eligiblePdp: false,
  giftAvailable: true,
  giftImage: '',
  currency: 'USD',
  oneTimeThreshold: 7500,
  subscriptionThreshold: 4500,
  propertyKey: '_refreshlabs_gwp',
  propertyValue: 'spawt-75-45',
  discountTitlePrefix: 'Free 16 oz sPAWt OFF',
  startAt: null,
  endAt: null,
  debug: false,
});

let busy = false;
let queued = false;
let reconcileTimer = null;
let lastState = null;
let unavailable = false;
let checkoutVerified = false;
let checkoutBlocked = false;
let commercialCheckoutBlocked = false;
let cartSettleGeneration = 0;
let cartSettlePending = false;

function readConfig() {
  const node = document.getElementById('RefreshLabsGwpConfig');
  if (!node) return DEFAULT_CONFIG;

  try {
    const supplied = JSON.parse(node.textContent || '{}');
    return Object.freeze({
      ...DEFAULT_CONFIG,
      ...supplied,
      giftVariantId: Number(supplied.giftVariantId || DEFAULT_CONFIG.giftVariantId),
      qualifyingProductIds: (supplied.qualifyingProductIds || DEFAULT_CONFIG.qualifyingProductIds).map(Number),
      collectionOffers: (supplied.collectionOffers || DEFAULT_CONFIG.collectionOffers)
        .map((offer) => ({ productId: Number(offer?.productId || 0), url: String(offer?.url || '') }))
        .filter((offer) => offer.productId && offer.url),
      currentProductId: Number(supplied.currentProductId || 0),
      eligiblePdp: Boolean(supplied.eligiblePdp),
      giftAvailable: supplied.giftAvailable !== false,
      currency: String(supplied.currency || DEFAULT_CONFIG.currency).toUpperCase(),
      oneTimeThreshold: Number(supplied.oneTimeThreshold || DEFAULT_CONFIG.oneTimeThreshold),
      subscriptionThreshold: Number(supplied.subscriptionThreshold || DEFAULT_CONFIG.subscriptionThreshold),
    });
  } catch (error) {
    console.error('[Refresh Labs GWP] Invalid configuration.', error);
    return DEFAULT_CONFIG;
  }
}

const config = readConfig();
const qualifyingProductIds = new Set(config.qualifyingProductIds);

function normalizeProductPath(value) {
  try {
    return new URL(value, window.location.origin).pathname.replace(/\/$/, '');
  } catch {
    return '';
  }
}

const collectionOffersByPath = new Map(
  config.collectionOffers
    .filter((offer) => qualifyingProductIds.has(offer.productId))
    .map((offer) => [normalizeProductPath(offer.url), offer.productId])
    .filter(([path]) => path)
);

function collectionOfferProductId(value) {
  return collectionOffersByPath.get(normalizeProductPath(value)) || null;
}

function isPreview() {
  const query = new URLSearchParams(window.location.search);
  return (
    window.Shopify?.designMode === true ||
    Boolean(document.getElementById('PBarNextFrame')) ||
    window.location.hostname.endsWith('.shopifypreview.com') ||
    query.has('preview_theme_id')
  );
}

function currencyIsSupported(currency = config.currency) {
  return String(currency || '').toUpperCase() === 'USD';
}

function offerIsOpen(now = Date.now()) {
  if (!config.enabled || !config.giftAvailable || !currencyIsSupported()) return false;
  if (isPreview()) return true;

  const startsAt = config.startAt ? Date.parse(config.startAt) : null;
  const endsAt = config.endAt ? Date.parse(config.endAt) : null;
  if (startsAt && now < startsAt) return false;
  if (endsAt && now > endsAt) return false;
  return true;
}

function isGiftVariant(item) {
  return Number(item.variant_id || item.id) === config.giftVariantId;
}

function isTaggedGift(item) {
  const properties = item.properties || {};
  return isGiftVariant(item) && String(properties[config.propertyKey]) === config.propertyValue;
}

function isCommercialBonusLine(item) {
  return String(item?.properties?._rl_drum_bonus || '') === 'v1';
}

function isSubscription(item) {
  return Boolean(item.selling_plan_allocation);
}

function qualifyingLinePrice(item) {
  if (Number.isFinite(Number(item.final_line_price))) return Number(item.final_line_price);
  if (Number.isFinite(Number(item.line_price))) return Number(item.line_price);
  return 0;
}

function qualifyingSubtotal(cart, purchaseType) {
  return (cart.items || []).reduce((total, item) => {
    if (isGiftVariant(item) || isCommercialBonusLine(item) || !qualifyingProductIds.has(Number(item.product_id))) return total;
    if (purchaseType === 'subscription' && !isSubscription(item)) return total;
    if (purchaseType === 'one-time' && isSubscription(item)) return total;
    return total + qualifyingLinePrice(item);
  }, 0);
}

function giftHasOfferDiscount(item) {
  if (!isGiftVariant(item)) return false;

  const discounts = [
    ...(item.discounts || []),
    ...(item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application || allocation),
  ];
  const titledOffer = discounts.some((discount) =>
    String(discount?.title || '').toLowerCase().startsWith(config.discountTitlePrefix.toLowerCase())
  );
  const original = Number(item.original_line_price ?? item.line_price ?? 0);
  const final = Number(item.final_line_price ?? item.line_price ?? 0);
  const quantity = Math.max(1, Number(item.quantity) || 1);
  const oneUnitPrice = original / quantity;
  const discountAmount = Math.max(0, original - final);

  // A generic cart discount must never be mistaken for the free gift offer.
  // The tagged auto-added line is quantity one, while a shopper's existing
  // paid sPAWt OFF line may contain multiple units with one unit discounted.
  if (isTaggedGift(item)) return final === 0;
  return final === 0 || (titledOffer && discountAmount + 1 >= oneUnitPrice);
}

function stateFromCart(cart) {
  const currencySupported = currencyIsSupported(cart.currency || config.currency);
  const oneTimeSubtotal = qualifyingSubtotal(cart, 'one-time');
  const subscriptionSubtotal = qualifyingSubtotal(cart, 'subscription');
  const qualifiesOneTime = currencySupported && oneTimeSubtotal >= config.oneTimeThreshold;
  const qualifiesSubscription = currencySupported && subscriptionSubtotal >= config.subscriptionThreshold;
  const giftLines = (cart.items || []).filter((item) => isGiftVariant(item) && !isCommercialBonusLine(item));
  const taggedGifts = giftLines.filter(isTaggedGift);

  const candidates = [];
  if (oneTimeSubtotal > 0) {
    candidates.push({
      type: 'one-time',
      threshold: config.oneTimeThreshold,
      subtotal: oneTimeSubtotal,
      remaining: Math.max(0, config.oneTimeThreshold - oneTimeSubtotal),
    });
  }
  if (subscriptionSubtotal > 0) {
    candidates.push({
      type: 'subscription',
      threshold: config.subscriptionThreshold,
      subtotal: subscriptionSubtotal,
      remaining: Math.max(0, config.subscriptionThreshold - subscriptionSubtotal),
    });
  }
  candidates.sort((a, b) => a.remaining - b.remaining || b.subtotal - a.subtotal);

  return {
    cart,
    currencySupported,
    oneTimeSubtotal,
    subscriptionSubtotal,
    qualifiesOneTime,
    qualifiesSubscription,
    qualifies: qualifiesOneTime || qualifiesSubscription,
    qualifyingTier: qualifiesSubscription ? 'subscription' : qualifiesOneTime ? 'one-time' : candidates[0]?.type || null,
    progress: candidates[0] || null,
    giftLines,
    taggedGifts,
    hasGift: giftLines.length > 0,
    giftIsDiscounted: taggedGifts.length
      ? taggedGifts.some(giftHasOfferDiscount)
      : giftLines.some(giftHasOfferDiscount),
  };
}

function money(cents) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: config.currency }).format(cents / 100);
}

function writeDebugState(label, state = null, extra = {}) {
  if (!config.debug) return;
  let node = document.getElementById('RefreshLabsGwpDebug');
  if (!node) {
    node = document.createElement('output');
    node.id = 'RefreshLabsGwpDebug';
    node.hidden = true;
    document.documentElement.append(node);
  }
  node.textContent = JSON.stringify({
    label,
    open: offerIsOpen(),
    oneTimeSubtotal: state?.oneTimeSubtotal ?? null,
    subscriptionSubtotal: state?.subscriptionSubtotal ?? null,
    qualifies: state?.qualifies ?? null,
    taggedGiftCount: state?.taggedGifts?.length ?? null,
    giftIsDiscounted: state?.giftIsDiscounted ?? null,
    unavailable,
    ...extra,
  });
}

class RefreshGwpStatus extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'status');
    this.setAttribute('aria-live', 'polite');
    this.setAttribute('aria-atomic', 'true');
    this.render(lastState);
  }

  render(state) {
    if (!checkoutBlocked && (!state || !offerIsOpen() || (!state.progress && !state.taggedGifts.length))) {
      this.hidden = true;
      this.replaceChildren();
      return;
    }

    this.hidden = false;
    const eyebrow = document.createElement('span');
    eyebrow.className = 'rl-gwp-status__eyebrow';
    eyebrow.textContent = 'Free sPAWt OFF offer';

    const message = document.createElement('strong');
    message.className = 'rl-gwp-status__message';

    const detail = document.createElement('span');
    detail.className = 'rl-gwp-status__detail';

    if (checkoutBlocked) {
      message.textContent = 'We could not safely verify the free gift.';
      detail.textContent = 'Please refresh this page and try again before checking out.';
    } else if (state.qualifies && state.giftIsDiscounted) {
      message.textContent = 'Your free 16 oz sPAWt OFF is in the cart.';
      detail.textContent = state.qualifiesSubscription
        ? 'It is a one-time gift in your first subscription order and will not renew.'
        : 'One complimentary bottle has been applied to this order.';
    } else if (state.qualifies && unavailable) {
      message.textContent = 'The free gift is temporarily unavailable.';
      detail.textContent = 'Your cart was left unchanged, so you will not be charged for an un-discounted gift.';
    } else if (state.qualifies) {
      message.textContent = 'Adding your free 16 oz sPAWt OFF…';
      detail.textContent = 'Your cart will update automatically.';
    } else if (state.progress) {
      const progress = state.progress;
      message.textContent = `${money(progress.remaining)} away from a free 16 oz sPAWt OFF.`;
      detail.textContent =
        progress.type === 'subscription'
          ? 'Qualifying subscription products count after the 15% subscription savings.'
          : 'Qualifying one-time LawN Order products count toward the $75 offer.';
    } else {
      message.textContent = 'Updating your free gift…';
      detail.textContent = 'Your cart will be ready in a moment.';
    }

    this.replaceChildren(eyebrow, message, detail);
  }
}

if (!customElements.get('refresh-gwp-status')) {
  customElements.define('refresh-gwp-status', RefreshGwpStatus);
}

function renderAllStatuses(state) {
  lastState = state;
  document.querySelectorAll('refresh-gwp-status').forEach((node) => node.render?.(state));
}

function renderPdpOffer() {
  const existingOffer = document.querySelector('[data-refresh-gwp-pdp-offer]');
  const form = config.eligiblePdp
    ? document.querySelector(
        `main product-form-component[data-product-id="${config.currentProductId}"], product-information product-form-component[data-product-id="${config.currentProductId}"]`
      )
    : null;

  if (!offerIsOpen() || !form) {
    document.documentElement.classList.remove('rl-gwp-eligible-pdp');
    existingOffer?.remove();
    return;
  }

  document.documentElement.classList.add('rl-gwp-eligible-pdp');
  if (existingOffer) return;

  const buyButtons = form.closest('.buy-buttons-block') || form.parentElement;
  if (!buyButtons) return;

  const offer = document.createElement('section');
  offer.className = 'rl-gwp-offer';
  offer.dataset.refreshGwpPdpOffer = '';
  offer.setAttribute('aria-label', 'Free sPAWt OFF offer');
  offer.innerHTML = `
    <div class="rl-gwp-offer__media" aria-hidden="true"></div>
    <div class="rl-gwp-offer__body">
      <p class="rl-gwp-offer__title"><span>Free 16 oz sPAWt OFF</span> for the next mess.</p>
      <div class="rl-gwp-offer__thresholds" aria-label="Free gift thresholds">
        <span><strong>$75+</strong> one-time</span>
        <span><strong>$45+</strong> first subscription</span>
      </div>
      <details class="rl-gwp-offer__details">
        <summary>Terms</summary>
        <p>Qualifying LawN Order Enzyme or Hydrogen Peroxide products only. One gift per order. Subscription gifts apply to the first shipment and do not renew. Confirm the gift appears at $0 in your cart before checkout.</p>
      </details>
    </div>
    <refresh-gwp-status hidden></refresh-gwp-status>
  `;

  const media = offer.querySelector('.rl-gwp-offer__media');
  if (config.giftImage && media) {
    const image = new Image();
    image.src = config.giftImage;
    image.alt = '';
    image.loading = 'lazy';
    image.width = 128;
    image.height = 128;
    media.append(image);
  } else {
    media?.setAttribute('hidden', '');
  }

  buyButtons.insertAdjacentElement('beforebegin', offer);
}

function isCollectionPage() {
  return /\/collections(?:\/|$)/.test(window.location.pathname);
}

function renderCollectionOffers() {
  const existingBadges = document.querySelectorAll('[data-refresh-gwp-collection-offer]');
  if (!offerIsOpen() || !isCollectionPage()) {
    existingBadges.forEach((badge) => badge.remove());
    return;
  }

  document.querySelectorAll('main a[href*="/products/"]').forEach((link) => {
    const productId = collectionOfferProductId(link.href);
    if (!productId) return;

    const card = link.closest(
      'product-card, .product-card, .product-card-wrapper, .card-wrapper, .card, li.grid__item'
    );
    if (!card || card.querySelector('[data-refresh-gwp-collection-offer]')) return;

    const details =
      card.querySelector(
        '.product-card__content, .product-card__information, .product-card__details, .card__content, .card__information, .card-information'
      ) || card;
    const badge = document.createElement('p');
    badge.className = 'rl-gwp-collection-badge';
    badge.dataset.refreshGwpCollectionOffer = '';
    badge.dataset.productId = String(productId);
    badge.setAttribute(
      'aria-label',
      'Offer: Free 16 ounce sPAWt OFF with a qualifying purchase of 75 dollars or more.'
    );
    badge.textContent = 'Free 16 oz sPAWt OFF at $75+';
    details.append(badge);
  });
}

function setCheckoutBusy(isBusy) {
  isBusy = Boolean(isBusy || commercialCheckoutBlocked);
  document.documentElement.classList.toggle('rl-gwp-syncing', isBusy);
  document.querySelectorAll('button[name="checkout"]').forEach((button) => {
    if (isBusy) {
      if (!('rlGwpWasDisabled' in button.dataset)) {
        button.dataset.rlGwpWasDisabled = button.disabled ? 'true' : 'false';
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      if (button.dataset.rlGwpWasDisabled === 'false') button.disabled = false;
      delete button.dataset.rlGwpWasDisabled;
      button.removeAttribute('aria-busy');
    }
  });
  document.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
    container.inert = isBusy;
    container.toggleAttribute('aria-busy', isBusy);
  });
  document.querySelectorAll('a[href*="/checkout"]').forEach((link) => {
    if (isBusy) {
      link.dataset.rlGwpCheckoutBusy = 'true';
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('aria-busy', 'true');
    } else {
      delete link.dataset.rlGwpCheckoutBusy;
      link.removeAttribute('aria-disabled');
      link.removeAttribute('aria-busy');
    }
  });
}

function checkoutIsVerifiedSafe() {
  return (
    checkoutVerified &&
    !checkoutBlocked &&
    !commercialCheckoutBlocked &&
    !drumBusy &&
    !cartSettlePending
  );
}

function markCartUnverified() {
  checkoutVerified = false;
  setCheckoutBusy(true);
}

async function requestCart(url, options = {}) {
  const { headers: suppliedHeaders = {}, ...requestOptions } = options;
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...requestOptions,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...suppliedHeaders },
  });
  if (!response.ok) throw new Error(`Cart request failed with ${response.status}.`);
  return response.json();
}

function getCart() {
  return requestCart(`${window.Shopify?.routes?.root || '/'}cart.js`, { method: 'GET' });
}

function addGift(tier) {
  return requestCart(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
    method: 'POST',
    body: JSON.stringify({
      items: [
        {
          id: config.giftVariantId,
          quantity: 1,
          properties: {
            [config.propertyKey]: config.propertyValue,
            _refreshlabs_gwp_tier: tier,
          },
        },
      ],
    }),
  });
}

function changeLine(key, quantity) {
  return requestCart(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
    method: 'POST',
    body: JSON.stringify({ id: key, quantity }),
  });
}

async function removeTaggedGifts(gifts) {
  for (const gift of gifts) await changeLine(gift.key, 0);
}

function dispatchCartUpdate(cart) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE_ID, {
      source: SOURCE_ID,
      itemCount: cart.item_count,
    })
  );

  // Horizon morphs the cart section after the event. Re-apply the live-region
  // message after those morphs so a qualifying or fail-safe status is not
  // replaced by the server-rendered empty custom element.
  [0, 160, 520].forEach((delay) => {
    window.setTimeout(() => renderAllStatuses(lastState), delay);
  });
}

async function reconcileGift() {
  if (!config.enabled) {
    checkoutVerified = true;
    checkoutBlocked = false;
    setCheckoutBusy(false);
    return;
  }
  if (busy) {
    queued = true;
    return;
  }

  busy = true;
  unavailable = false;
  checkoutBlocked = false;
  setCheckoutBusy(true);

  return cartCoordinator.run(async () => {
  try {
    let cart = await getCart();
    let state = stateFromCart(cart);
    let changed = false;
    writeDebugState('initial', state);

    if (!offerIsOpen() || !state.currencySupported) {
      if (state.taggedGifts.length) {
        await removeTaggedGifts(state.taggedGifts);
        cart = await getCart();
        state = stateFromCart(cart);
        changed = true;
      }
      renderAllStatuses(null);
      if (changed) dispatchCartUpdate(cart);
      checkoutVerified = true;
      return;
    }

    if (!state.qualifies && state.taggedGifts.length) {
      await removeTaggedGifts(state.taggedGifts);
      cart = await getCart();
      state = stateFromCart(cart);
      changed = true;
    } else if (state.qualifies) {
      const primaryTaggedGift = state.taggedGifts[0];
      const extraTaggedGifts = state.taggedGifts.slice(1);

      if (extraTaggedGifts.length) {
        await removeTaggedGifts(extraTaggedGifts);
        changed = true;
      }
      if (primaryTaggedGift && Number(primaryTaggedGift.quantity) !== 1) {
        await changeLine(primaryTaggedGift.key, 1);
        changed = true;
      }
      if (!state.giftIsDiscounted && !state.taggedGifts.length) {
        await addGift(state.qualifyingTier || 'one-time');
        changed = true;
      }

      if (changed) cart = await getCart();
      state = stateFromCart(cart);

      const unsafeTaggedGift = state.taggedGifts.some((gift) => !giftHasOfferDiscount(gift));
      if (unsafeTaggedGift) {
        await removeTaggedGifts(state.taggedGifts);
        cart = await getCart();
        state = stateFromCart(cart);
        unavailable = !state.giftIsDiscounted;
        changed = true;
      }

      if (state.taggedGifts.some((gift) => !giftHasOfferDiscount(gift))) {
        throw new Error('The gift price could not be verified.');
      }
    }

    renderAllStatuses(state);
    writeDebugState('complete', state, { changed });
    if (changed) dispatchCartUpdate(cart);
    checkoutVerified = true;
  } catch (error) {
    unavailable = true;
    writeDebugState('error', lastState, { message: String(error?.message || error) });
    console.error('[Refresh Labs GWP]', error);
    try {
      let recoveryCart = await getCart();
      let recoveryState = stateFromCart(recoveryCart);
      if (recoveryState.taggedGifts.length) {
        await removeTaggedGifts(recoveryState.taggedGifts);
        recoveryCart = await getCart();
        recoveryState = stateFromCart(recoveryCart);
        dispatchCartUpdate(recoveryCart);
      }

      if (recoveryState.taggedGifts.length) throw new Error('The gift line could not be removed.');
      renderAllStatuses(recoveryState);
      checkoutVerified = true;
    } catch (recoveryError) {
      checkoutVerified = false;
      checkoutBlocked = true;
      console.error('[Refresh Labs GWP] Checkout paused for cart safety.', recoveryError);
      renderAllStatuses(lastState);
    }
  } finally {
    setCheckoutBusy(cartSettlePending || !checkoutVerified || checkoutBlocked);
    busy = false;
    if (queued) {
      queued = false;
      scheduleReconcile(0);
    }
  }
  });
}

function waitForCartSettle(delay) {
  return new Promise((resolve) => window.setTimeout(resolve, delay));
}

async function reconcileWhenIdle(generation) {
  while (busy && generation === cartSettleGeneration) {
    await waitForCartSettle(25);
  }
  if (generation !== cartSettleGeneration) return;
  await reconcileGift();
}

async function reconcileAfterExternalCartUpdate() {
  const generation = ++cartSettleGeneration;
  cartSettlePending = true;
  markCartUnverified();

  // Subscription pricing and automatic Buy X Get Y discounts can settle in
  // separate Shopify cart updates. Verify once promptly, then once after the
  // discount engine has caught up so a former free gift cannot remain paid.
  for (const delay of CART_SETTLE_DELAYS) {
    await waitForCartSettle(delay);
    if (generation !== cartSettleGeneration) return;
    await reconcileWhenIdle(generation);
  }

  if (generation !== cartSettleGeneration) return;
  cartSettlePending = false;
  setCheckoutBusy(!checkoutVerified || checkoutBlocked);
}

function scheduleReconcile(delay = 80) {
  markCartUnverified();
  window.clearTimeout(reconcileTimer);
  reconcileTimer = window.setTimeout(reconcileGift, delay);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.sourceId === SOURCE_ID) return;
  void reconcileAfterExternalCartUpdate();
});
document.addEventListener(ThemeEvents.discountUpdate, () => {
  void reconcileAfterExternalCartUpdate();
});
document.addEventListener('shopify:section:load', () => {
  renderPdpOffer();
  renderCollectionOffers();
  scheduleReconcile(0);
});
window.addEventListener('pageshow', () => scheduleReconcile(0));

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target.closest('button[name="checkout"], a[href*="/checkout"]') : null;
    if (!target || checkoutIsVerifiedSafe()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    scheduleReconcile(0);
    scheduleDrumReconcile(0);
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const submitter = event.submitter;
    if (submitter?.name !== 'checkout' || checkoutIsVerifiedSafe()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    scheduleReconcile(0);
    scheduleDrumReconcile(0);
  },
  true
);

let previewDetected = isPreview();
let collectionOfferTimer = null;

new MutationObserver(() => {
  if (!previewDetected && document.getElementById('PBarNextFrame')) {
    previewDetected = true;
    renderPdpOffer();
    renderCollectionOffers();
    scheduleReconcile(0);
    return;
  }
  if (isCollectionPage()) {
    window.clearTimeout(collectionOfferTimer);
    collectionOfferTimer = window.setTimeout(renderCollectionOffers, 80);
  }
  if (!checkoutVerified) setCheckoutBusy(true);
}).observe(document.documentElement, { childList: true, subtree: true });

function scheduleOfferBoundaryRefresh() {
  const now = Date.now();
  const maximumDelay = 2_147_000_000;
  [config.startAt, config.endAt].forEach((boundary) => {
    if (!boundary) return;
    const delay = Date.parse(boundary) - now + 1000;
    if (delay <= 0 || delay > maximumDelay) return;
    window.setTimeout(() => {
      renderPdpOffer();
      renderCollectionOffers();
      scheduleReconcile(0);
    }, delay);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      renderPdpOffer();
      renderCollectionOffers();
      reconcileGift();
      scheduleOfferBoundaryRefresh();
    },
    { once: true }
  );
} else {
  renderPdpOffer();
  renderCollectionOffers();
  reconcileGift();
  scheduleOfferBoundaryRefresh();
}

if (config.debug) {
  window.__refreshLabsGwpTest = {
    getCart,
    isGiftVariant,
    isTaggedGift,
    offerIsOpen,
    currencyIsSupported,
    normalizeProductPath,
    collectionOfferProductId,
    qualifyingSubtotal,
    stateFromCart,
    reconcileGift,
    reconcileAfterExternalCartUpdate,
  };
}


/* Refresh Labs commercial bulk bonus controller — staged 2026-09-03 */
const DRUM_DEFAULT_CONFIG = Object.freeze({
  enabled: false,
  currentProductId: 0,
  currentProductHandle: '',
  qualifyingProductIds: [],
  propertyKey: '_rl_drum_bonus',
  propertyValue: 'v1',
  choicePropertyKey: '_rl_drum_bonus_choice',
  defaultChoice: 'enzyme',
  discountCodes: {},
  options: [],
});

function readDrumConfig() {
  const node = document.getElementById('RefreshLabsDrumBonusConfig');
  if (!node) return DRUM_DEFAULT_CONFIG;

  try {
    const supplied = JSON.parse(node.textContent || '{}');
    return Object.freeze({
      ...DRUM_DEFAULT_CONFIG,
      ...supplied,
      currentProductId: Number(supplied.currentProductId || 0),
      qualifyingProductIds: (supplied.qualifyingProductIds || []).map(Number).filter(Boolean),
      discountCodes: Object.fromEntries(
        Object.entries(supplied.discountCodes || {}).map(([key, value]) => [String(key), String(value || '').trim()])
      ),
      options: (supplied.options || [])
        .map((option) => ({
          key: String(option?.key || ''),
          label: String(option?.label || ''),
          value: String(option?.value || ''),
          image: String(option?.image || ''),
          imageSecondary: String(option?.imageSecondary || ''),
          available: option?.available === true,
          variantIds: (option?.variantIds || []).map(Number).filter(Boolean),
        }))
        .filter((option) => option.key && option.variantIds.length),
    });
  } catch (error) {
    console.error('[Refresh Labs Drum Bonus] Invalid configuration.', error);
    return DRUM_DEFAULT_CONFIG;
  }
}

const drumConfig = readDrumConfig();
const drumQualifierProductIds = new Set(drumConfig.qualifyingProductIds);
const drumOptionsByKey = new Map(drumConfig.options.map((option) => [option.key, option]));
const drumGiftVariantIds = new Set(drumConfig.options.flatMap((option) => option.variantIds));
const drumOfferCodes = new Set(Object.values(drumConfig.discountCodes).filter(Boolean).map((code) => code.toUpperCase()));
const DRUM_CHOICE_STORAGE_KEY = 'refreshlabs_drum_bonus_choice_v1';
let drumBusy = false;
let drumQueued = false;
let drumReconcileTimer = null;
let drumDiscountUnavailable = false;
let drumRememberedChoice = (() => {
  try {
    return window.localStorage.getItem(DRUM_CHOICE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
})();
let drumSelectedChoice = drumRememberedChoice || drumConfig.defaultChoice;

function rememberDrumChoice(choice = '') {
  drumRememberedChoice = choice;
  try {
    if (choice) window.localStorage.setItem(DRUM_CHOICE_STORAGE_KEY, choice);
    else window.localStorage.removeItem(DRUM_CHOICE_STORAGE_KEY);
  } catch {
    // The cart remains authoritative when storage is unavailable.
  }
}

function drumPicker() {
  return document.querySelector('[data-rl-drum-bonus-picker]');
}

function drumStatus(message = '', error = false) {
  const picker = drumPicker();
  const status = picker?.querySelector('[data-rl-drum-bonus-status]');
  if (status) status.textContent = message;
  picker?.classList.toggle('has-error', Boolean(error));
}

function availableDrumOption(key) {
  const requested = drumOptionsByKey.get(String(key || ''));
  if (requested?.available) return requested;
  return drumConfig.options.find((option) => option.available) || null;
}

function selectedDrumOption() {
  const selected = drumPicker()?.querySelector('input[name="rl-drum-bonus-choice"]:checked')?.value;
  return availableDrumOption(selected || drumSelectedChoice || drumConfig.defaultChoice);
}

function setDrumBusy(isBusy) {
  drumBusy = isBusy;
  if (isBusy) commercialCheckoutBlocked = true;
  const picker = drumPicker();
  picker?.classList.toggle('is-busy', isBusy);
  picker?.toggleAttribute('aria-busy', isBusy);

  const details = document.querySelector('[data-testid="product-information-details"], [id^="ProductInformation-"]');
  const nativeButton = details?.querySelector('form[action*="/cart/add"] button[name="add"], form[action*="/cart/add"] button[type="submit"]');
  const stickyButton = document.querySelector('[data-rl-drum-sticky-button]');
  [nativeButton, stickyButton].filter(Boolean).forEach((button) => {
    if (isBusy) {
      if (!('rlDrumWasDisabled' in button.dataset)) button.dataset.rlDrumWasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      if (button.dataset.rlDrumWasDisabled === 'false') button.disabled = false;
      delete button.dataset.rlDrumWasDisabled;
      button.removeAttribute('aria-busy');
    }
  });
  setCheckoutBusy(isBusy || checkoutBlocked || cartSettlePending || !checkoutVerified);
}

function syncDrumPicker() {
  const picker = drumPicker();
  if (!picker || !drumConfig.enabled) return;

  const checked = picker.querySelector('input[name="rl-drum-bonus-choice"]:checked');
  let option = availableDrumOption(checked?.value || drumSelectedChoice || drumConfig.defaultChoice);
  if (!option) {
    picker.hidden = true;
    drumStatus('The professional bonus is temporarily unavailable.', true);
    document.dispatchEvent(
      new CustomEvent('refreshlabs:drum-bonus-availability', { detail: { available: false } })
    );
    return;
  }

  picker.hidden = false;
  document.dispatchEvent(
    new CustomEvent('refreshlabs:drum-bonus-availability', { detail: { available: true } })
  );
  drumSelectedChoice = option.key;
  const matchingInput = picker.querySelector(`input[name="rl-drum-bonus-choice"][value="${CSS.escape(option.key)}"]`);
  if (matchingInput && !matchingInput.checked) matchingInput.checked = true;
  const label = picker.querySelector('[data-rl-drum-bonus-selected-label]');
  const value = picker.querySelector('[data-rl-drum-bonus-selected-value]');
  const image = picker.querySelector('[data-rl-drum-bonus-selected-image]');
  const imageSecondary = picker.querySelector('[data-rl-drum-bonus-selected-image-secondary]');
  const selectedMedia = picker.querySelector('.rl-drum-bonus__selected-media');
  if (label) label.textContent = option.label;
  if (value) value.textContent = option.value;
  if (image) {
    if (option.image) {
      image.src = option.image;
      image.hidden = false;
    } else {
      image.removeAttribute('src');
      image.hidden = true;
    }
  }
  if (imageSecondary) {
    if (option.imageSecondary) {
      imageSecondary.src = option.imageSecondary;
      imageSecondary.hidden = false;
    } else {
      imageSecondary.removeAttribute('src');
      imageSecondary.hidden = true;
    }
  }
  selectedMedia?.classList.toggle('is-duo', Boolean(option.imageSecondary));

  const oppositeFormula =
    (drumConfig.currentProductHandle === 'lawn-order-enzyme-based-turf-grass-concrete-cleaner-bulk-drums' && option.key === 'hydrogen') ||
    (drumConfig.currentProductHandle === 'lawn-order-hydrogen' && option.key === 'enzyme');
  const boundary = picker.querySelector('[data-rl-drum-bonus-boundary]');
  if (boundary) boundary.hidden = !oppositeFormula;
}

function initializeDrumPicker() {
  const picker = drumPicker();
  if (!picker || !drumConfig.enabled || picker.dataset.rlDrumBonusInitialized === 'true') return;
  picker.dataset.rlDrumBonusInitialized = 'true';

  const configuredDefault = availableDrumOption(drumConfig.defaultChoice);
  if (configuredDefault) drumSelectedChoice = configuredDefault.key;
  syncDrumPicker();

  picker.addEventListener('change', (event) => {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    if (!input?.matches('input[name="rl-drum-bonus-choice"]')) return;
    drumSelectedChoice = input.value;
    rememberDrumChoice(input.value);
    drumDiscountUnavailable = false;
    drumStatus('');
    syncDrumPicker();
  });
}

function isDrumQualifier(item) {
  return drumQualifierProductIds.has(Number(item?.product_id));
}

function isDrumTaggedGift(item) {
  const properties = item?.properties || {};
  return (
    drumGiftVariantIds.has(Number(item?.variant_id || item?.id)) &&
    String(properties[drumConfig.propertyKey] || '') === drumConfig.propertyValue
  );
}

function drumLineChoice(item) {
  return String(item?.properties?.[drumConfig.choicePropertyKey] || '');
}

function cartDiscountCodes(cart) {
  return (cart?.discount_codes || [])
    .map((discount) => String(discount?.code || '').trim())
    .filter(Boolean);
}

function sameStringList(a, b) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

async function setDrumOfferCode(cart, selectedCode = '') {
  const existing = cartDiscountCodes(cart);
  const preserved = existing.filter((code) => !drumOfferCodes.has(code.toUpperCase()));
  const next = selectedCode ? [...preserved, selectedCode] : preserved;
  if (sameStringList(existing, next)) return cart;
  return requestCart(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    body: JSON.stringify({ discount: next.join(',') }),
  });
}

function drumGiftItems(option) {
  return option.variantIds.map((variantId) => ({
    id: variantId,
    quantity: 1,
    properties: {
      [drumConfig.propertyKey]: drumConfig.propertyValue,
      [drumConfig.choicePropertyKey]: option.key,
    },
  }));
}

function taggedDrumLines(cart) {
  return (cart?.items || []).filter(isDrumTaggedGift);
}

async function removeDrumLines(lines) {
  for (const line of lines) await changeLine(line.key, 0);
}

function choiceFromTaggedLines(lines) {
  const counts = new Map();
  lines.forEach((line) => {
    const key = drumLineChoice(line);
    if (drumOptionsByKey.has(key)) counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || '';
}

function defaultChoiceForCart(cart) {
  const remembered = drumOptionsByKey.get(drumRememberedChoice);
  if (remembered?.available) return remembered.key;
  const products = new Set((cart?.items || []).filter(isDrumQualifier).map((item) => Number(item.product_id)));
  if (products.has(8376106451136) && !products.has(8436768932032)) return 'hydrogen';
  if (products.has(8436768932032)) return 'enzyme';
  return drumConfig.defaultChoice;
}

function taggedChoiceIsExact(cart, option) {
  const lines = taggedDrumLines(cart);
  if (lines.length !== option.variantIds.length) return false;
  return option.variantIds.every((variantId) => {
    const matches = lines.filter(
      (line) => Number(line.variant_id || line.id) === variantId && drumLineChoice(line) === option.key
    );
    return matches.length === 1 && Number(matches[0].quantity) === 1;
  });
}

function taggedChoiceIsFree(cart, option) {
  if (!taggedChoiceIsExact(cart, option)) return false;
  return taggedDrumLines(cart).every((line) => {
    const quantity = Math.max(1, Number(line.quantity) || 1);
    const finalLinePrice = Number(line.final_line_price ?? line.line_price ?? 0);
    const finalUnitPrice = Number(line.final_price ?? finalLinePrice / quantity);
    return finalLinePrice === 0 && finalUnitPrice === 0;
  });
}

async function normalizeDrumGift(cart, option) {
  let changed = false;
  const tagged = taggedDrumLines(cart);
  const expected = new Set(option.variantIds);
  const keepKeys = new Set();

  for (const variantId of option.variantIds) {
    const matching = tagged.filter(
      (line) => Number(line.variant_id || line.id) === variantId && drumLineChoice(line) === option.key
    );
    if (matching[0]) keepKeys.add(matching[0].key);
  }

  const remove = tagged.filter((line) => !expected.has(Number(line.variant_id || line.id)) || !keepKeys.has(line.key));
  if (remove.length) {
    await removeDrumLines(remove);
    changed = true;
    cart = await getCart();
  }

  const current = taggedDrumLines(cart);
  for (const line of current) {
    if (option.variantIds.includes(Number(line.variant_id || line.id)) && Number(line.quantity) !== 1) {
      await changeLine(line.key, 1);
      changed = true;
    }
  }
  if (changed) cart = await getCart();

  const currentIds = new Set(taggedDrumLines(cart).map((line) => Number(line.variant_id || line.id)));
  const missing = drumGiftItems(option).filter((item) => !currentIds.has(item.id));
  if (missing.length) {
    await requestCart(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      body: JSON.stringify({ items: missing }),
    });
    changed = true;
    cart = await getCart();
  }

  return { cart, changed };
}

function dispatchDrumCartUpdate(cart) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, DRUM_SOURCE_ID, {
      source: DRUM_SOURCE_ID,
      itemCount: cart.item_count,
    })
  );
}

async function failSafeDrumBonus(cart, message) {
  let recovered = cart;
  const tagged = taggedDrumLines(recovered);
  if (tagged.length) {
    await removeDrumLines(tagged);
    recovered = await getCart();
  }
  recovered = await setDrumOfferCode(recovered, '');
  recovered = await getCart();
  if (taggedDrumLines(recovered).length) throw new Error('The paid bonus line could not be removed.');
  const lingeringOwnCode = cartDiscountCodes(recovered).some((code) => drumOfferCodes.has(code.toUpperCase()));
  if (lingeringOwnCode) throw new Error('The drum bonus discount could not be removed.');
  drumDiscountUnavailable = true;
  drumStatus(message, true);
  dispatchDrumCartUpdate(recovered);
  return recovered;
}

async function reconcileDrumBonus() {
  if (!drumConfig.enabled) return;
  if (drumBusy) {
    drumQueued = true;
    return;
  }

  setDrumBusy(true);
  return cartCoordinator.run(async () => {
    try {
      let cart = await getCart();
      const qualifies = (cart.items || []).some(isDrumQualifier);
      const tagged = taggedDrumLines(cart);
      let changed = false;

      if (!qualifies) {
        if (tagged.length) {
          await removeDrumLines(tagged);
          cart = await getCart();
          changed = true;
        }
        const beforeCodes = cartDiscountCodes(cart);
        cart = await setDrumOfferCode(cart, '');
        if (!sameStringList(beforeCodes, cartDiscountCodes(cart))) changed = true;
        if (changed) dispatchDrumCartUpdate(cart);
        drumStatus('');
        rememberDrumChoice('');
        commercialCheckoutBlocked = false;
        return;
      }

      const existingChoice = choiceFromTaggedLines(tagged);
      const option = availableDrumOption(existingChoice || defaultChoiceForCart(cart));
      if (!option || !drumConfig.discountCodes[option.key]) {
        if (tagged.length) cart = await failSafeDrumBonus(cart, 'The free professional bonus is temporarily unavailable. Your bulk item remains in the cart.');
        commercialCheckoutBlocked = false;
        return;
      }

      if (drumDiscountUnavailable && !tagged.length) {
        commercialCheckoutBlocked = false;
        return;
      }

      const normalized = await normalizeDrumGift(cart, option);
      cart = normalized.cart;
      changed = normalized.changed;
      const beforeCodes = cartDiscountCodes(cart);
      cart = await setDrumOfferCode(cart, drumConfig.discountCodes[option.key]);
      if (!sameStringList(beforeCodes, cartDiscountCodes(cart))) changed = true;
      await waitForCartSettle(180);
      cart = await getCart();

      if (!taggedChoiceIsFree(cart, option)) {
        await failSafeDrumBonus(
          cart,
          'We could not verify the selected bonus at $0, so it was removed. Your bulk item remains in the cart.'
        );
        commercialCheckoutBlocked = false;
        return;
      }

      if (changed) dispatchDrumCartUpdate(cart);
      rememberDrumChoice(option.key);
      drumStatus(`${option.label} is confirmed at $0 in your cart.`);
      commercialCheckoutBlocked = false;
    } catch (error) {
      console.error('[Refresh Labs Drum Bonus]', error);
      try {
        const recoveryCart = await getCart();
        await failSafeDrumBonus(
          recoveryCart,
          'We could not safely add the free bonus. Your bulk item remains in the cart without a paid gift.'
        );
        commercialCheckoutBlocked = false;
      } catch (recoveryError) {
        commercialCheckoutBlocked = true;
        drumStatus('Checkout is paused because the bonus price could not be verified. Please refresh and try again.', true);
        console.error('[Refresh Labs Drum Bonus] Checkout paused for cart safety.', recoveryError);
      }
    } finally {
      setDrumBusy(false);
      if (drumQueued) {
        drumQueued = false;
        scheduleDrumReconcile(0);
      }
    }
  });
}

function scheduleDrumReconcile(delay = 160) {
  if (!drumConfig.enabled) return;
  commercialCheckoutBlocked = true;
  setCheckoutBusy(true);
  window.clearTimeout(drumReconcileTimer);
  drumReconcileTimer = window.setTimeout(() => void reconcileDrumBonus(), delay);
}

function productItemFromForm(form) {
  const data = new FormData(form);
  const id = Number(data.get('id'));
  if (!id) throw new Error('The selected bulk option could not be identified.');
  const item = { id, quantity: Math.max(1, Number(data.get('quantity')) || 1) };
  const properties = {};
  for (const [name, value] of data.entries()) {
    const match = String(name).match(/^properties\[(.+)]$/);
    if (match && String(value)) properties[match[1]] = String(value);
  }
  if (Object.keys(properties).length) item.properties = properties;
  const sellingPlan = Number(data.get('selling_plan'));
  if (sellingPlan) item.selling_plan = sellingPlan;
  return item;
}

async function restoreExactDrumBonus(cart, option) {
  if (!option || !drumConfig.discountCodes[option.key]) return null;
  const normalized = await normalizeDrumGift(cart, option);
  let restored = await setDrumOfferCode(normalized.cart, drumConfig.discountCodes[option.key]);
  await waitForCartSettle(180);
  restored = await getCart();
  return taggedChoiceIsFree(restored, option) ? restored : null;
}

async function addBulkOrderWithoutBonus(form) {
  setDrumBusy(true);
  let bulkAddCompleted = false;
  return cartCoordinator.run(async () => {
    try {
      const productItem = productItemFromForm(form);
      await requestCart(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        body: JSON.stringify({ items: [productItem] }),
      });
      bulkAddCompleted = true;

      let cart = await getCart();
      const tagged = taggedDrumLines(cart);
      if (tagged.length) {
        await removeDrumLines(tagged);
        cart = await getCart();
      }
      cart = await setDrumOfferCode(cart, '');
      cart = await getCart();
      dispatchDrumCartUpdate(cart);
      drumStatus('Your bulk item was added. The professional bonus is temporarily unavailable.', true);
      commercialCheckoutBlocked = false;
    } catch (error) {
      console.error('[Refresh Labs Drum Bonus] Bulk-only add failed.', error);
      try {
        const recoveryCart = await getCart();
        const recoveryChoice = choiceFromTaggedLines(taggedDrumLines(recoveryCart));
        const recoveryOption = drumOptionsByKey.get(recoveryChoice);
        if (recoveryOption && taggedChoiceIsFree(recoveryCart, recoveryOption)) {
          drumStatus(
            bulkAddCompleted
              ? `Your bulk item was added. Your existing ${recoveryOption.label} bonus remains at $0.`
              : `We could not confirm the bulk-item update. Please review your cart before trying again. Your existing ${recoveryOption.label} bonus remains at $0.`,
            true
          );
          dispatchDrumCartUpdate(recoveryCart);
          commercialCheckoutBlocked = false;
        } else {
          await failSafeDrumBonus(
            recoveryCart,
            bulkAddCompleted
              ? 'Your bulk item was added without a bonus. No paid bonus was left in your cart.'
              : 'We could not confirm the bulk-item update. Please review your cart before trying again. No paid bonus was left in your cart.'
          );
          commercialCheckoutBlocked = false;
        }
      } catch (recoveryError) {
        commercialCheckoutBlocked = true;
        drumStatus('Checkout is paused because the bonus price could not be verified. Please refresh and try again.', true);
        console.error('[Refresh Labs Drum Bonus] Bulk-only recovery failed.', recoveryError);
      }
    } finally {
      setDrumBusy(false);
      if (drumQueued) {
        drumQueued = false;
        scheduleDrumReconcile(0);
      }
    }
  });
}

async function addBulkOrderWithBonus(form) {
  const option = selectedDrumOption();
  if (!option || !option.available) {
    return addBulkOrderWithoutBonus(form);
  }
  const discountCode = drumConfig.discountCodes[option.key];
  if (!discountCode) {
    drumStatus('The selected bonus is not ready yet. Please choose another option or contact us.', true);
    return;
  }

  setDrumBusy(true);
  drumStatus(`Adding your bulk order with ${option.label}…`);
  let priorOption = null;
  let priorWasFree = false;
  let bulkAddCompleted = false;
  return cartCoordinator.run(async () => {
    try {
      let cart = await getCart();
      const priorChoice = choiceFromTaggedLines(taggedDrumLines(cart));
      priorOption = drumOptionsByKey.get(priorChoice) || null;
      priorWasFree = Boolean(priorOption && taggedChoiceIsFree(cart, priorOption));
      const productItem = productItemFromForm(form);
      await requestCart(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        body: JSON.stringify({ items: [productItem, ...drumGiftItems(option)] }),
      });
      bulkAddCompleted = true;
      cart = await getCart();
      const normalized = await normalizeDrumGift(cart, option);
      cart = normalized.cart;
      cart = await setDrumOfferCode(cart, discountCode);
      await waitForCartSettle(180);
      cart = await getCart();

      if (!taggedChoiceIsFree(cart, option)) {
        await failSafeDrumBonus(
          cart,
          'Your bulk item was added, but the bonus could not be verified at $0 and was removed. Please contact us for help.'
        );
        commercialCheckoutBlocked = false;
        return;
      }

      drumDiscountUnavailable = false;
      rememberDrumChoice(option.key);
      drumStatus(`${option.label} is confirmed at $0 in your cart.`);
      const chooser = drumPicker()?.querySelector('[data-rl-drum-bonus-details]');
      if (chooser) chooser.open = false;
      dispatchDrumCartUpdate(cart);
      commercialCheckoutBlocked = false;
    } catch (error) {
      console.error('[Refresh Labs Drum Bonus] Add failed.', error);
      try {
        let recoveryCart = await getCart();
        if (priorWasFree && priorOption) {
          const restored = await restoreExactDrumBonus(recoveryCart, priorOption);
          if (restored) {
            rememberDrumChoice(priorOption.key);
            drumStatus(
              bulkAddCompleted
                ? `Your bulk item was added. Your existing ${priorOption.label} bonus remains at $0.`
                : `We could not confirm the cart update. Please review your cart before trying again. Your existing ${priorOption.label} bonus remains at $0.`,
              true
            );
            dispatchDrumCartUpdate(restored);
            commercialCheckoutBlocked = false;
            return;
          }
          recoveryCart = await getCart();
        }
        const recoveryChoice = choiceFromTaggedLines(taggedDrumLines(recoveryCart));
        const recoveryOption = drumOptionsByKey.get(recoveryChoice);
        if (recoveryOption && taggedChoiceIsFree(recoveryCart, recoveryOption)) {
          rememberDrumChoice(recoveryOption.key);
          drumStatus(
            `We could not complete the bonus update. Please review your cart before trying again. Your existing ${recoveryOption.label} bonus remains at $0.`,
            true
          );
          dispatchDrumCartUpdate(recoveryCart);
          commercialCheckoutBlocked = false;
        } else {
          await failSafeDrumBonus(
            recoveryCart,
            'We could not complete the bonus update. Please review your cart before trying again. No paid bonus was left in your cart.'
          );
          commercialCheckoutBlocked = false;
        }
      } catch (recoveryError) {
        commercialCheckoutBlocked = true;
        drumStatus('Checkout is paused because the bonus price could not be verified. Please refresh and try again.', true);
        console.error('[Refresh Labs Drum Bonus] Add recovery failed.', recoveryError);
      }
    } finally {
      setDrumBusy(false);
      if (drumQueued) {
        drumQueued = false;
        scheduleDrumReconcile(0);
      }
    }
  });
}

if (drumConfig.enabled) {
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form?.matches('form[action*="/cart/add"]') || !drumPicker()) return;
      const details = form.closest('[data-testid="product-information-details"], [id^="ProductInformation-"]');
      if (!details || !details.contains(drumPicker())) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!drumBusy) void addBulkOrderWithBonus(form);
    },
    true
  );

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.sourceId === DRUM_SOURCE_ID) return;
    scheduleDrumReconcile(event.detail?.sourceId === SOURCE_ID ? 220 : 140);
  });
  document.addEventListener(ThemeEvents.discountUpdate, () => scheduleDrumReconcile(220));
  document.addEventListener('shopify:section:load', () => {
    initializeDrumPicker();
    scheduleDrumReconcile(0);
  });
  window.addEventListener('pageshow', () => scheduleDrumReconcile(0));

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeDrumPicker();
        scheduleDrumReconcile(0);
      },
      { once: true }
    );
  } else {
    initializeDrumPicker();
    scheduleDrumReconcile(0);
  }
}


/* RL storefront accessibility bridge 2026-09-02 */
(() => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;

  const syncSlide = (slide) => {
    if (!slide?.matches?.('slideshow-slide[aria-hidden]')) return;
    slide.toggleAttribute('inert', slide.getAttribute('aria-hidden') === 'true');
  };

  document.querySelectorAll('slideshow-slide[aria-hidden]').forEach(syncSlide);
  const slideObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => syncSlide(mutation.target));
  });
  slideObserver.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden']
  });

  const watchedForms = new WeakSet();
  const bindForm = (host) => {
    if (watchedForms.has(host)) return;
    watchedForms.add(host);
    const initializeRoot = () => {
      if (!host.isConnected) {
        watchedForms.delete(host);
        return;
      }
      const root = host.shadowRoot;
      if (!root) {
        window.setTimeout(initializeRoot, 1000);
        return;
      }
      const labelDialog = () => {
      const dialog = root.querySelector('[role="dialog"], [role="alertdialog"]');
      if (dialog && !dialog.hasAttribute('aria-label') && !dialog.hasAttribute('aria-labelledby')) {
        dialog.setAttribute('aria-label', 'Refresh Labs signup');
      }
      };
      labelDialog();
      const dialogObserver = new MutationObserver(labelDialog);
      dialogObserver.observe(root, {childList: true, subtree: true});
      if (!root.getElementById('refresh-labs-forms-a11y')) {
        const style = document.createElement('style');
        style.id = 'refresh-labs-forms-a11y';
        style.textContent = `
          label { color: var(--form-text-color, #202020) !important; text-shadow: none !important; }
          @media (max-width: 749px) {
            [data-form-root="true"][data-current-step="tease"] {
              bottom: var(--rl-mobile-purchase-offset, 0px) !important;
              padding: 8px 12px !important;
            }
            [data-testid="form-teaser"] { max-width: calc(100vw - 24px); }
          }
        `;
        root.append(style);
      }
    };
    if (window.customElements && !window.customElements.get('shopify-forms-embed')) {
      window.customElements.whenDefined('shopify-forms-embed').then(initializeRoot);
    } else {
      initializeRoot();
    }
  };
  const findForms = (node) => {
    if (node.nodeType !== 1) return;
    if (node.matches('shopify-forms-embed')) bindForm(node);
    node.querySelectorAll('shopify-forms-embed').forEach(bindForm);
  };
  findForms(document.documentElement);
  const formsObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach(findForms));
  });
  formsObserver.observe(document.documentElement, {childList: true, subtree: true});
})();

(() => {
  const selector = '[data-refresh-gwp-pdp-offer]';
  const keepSingleOffer = () => {
    const offers = Array.from(document.querySelectorAll(selector));
    const preferred = offers.find((offer) => offer.hasAttribute('data-refresh-gwp-server-offer')) || offers[0];
    offers.forEach((offer) => {
      if (offer !== preferred) offer.remove();
    });
  };
  keepSingleOffer();
  const offerObserver = new MutationObserver((mutations) => {
    const offerAdded = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) =>
      node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))
    ));
    if (offerAdded) keepSingleOffer();
  });
  offerObserver.observe(document.documentElement, {childList: true, subtree: true});
})();
